import React, { useState, useEffect } from 'react';  
import {  
  Table, Button, message, Space,  
  Card, Typography, List, Row, Col, Modal, Collapse, Divider, Flex  
} from 'antd';  
import { PlusOutlined, MinusOutlined, DeleteOutlined } from '@ant-design/icons';  
  
import { deleteGroup, searchGroups } from '../../api/group';  
import { getGroupEvents } from '../../api/event';  
import { getApplicationsByGroup, createApplication, deleteApplication } from '../../api/application';  
import { loadState } from "../../stores/store";  
  
import CreateGroupModal from '../Groups/createGroupModal'; 
import './groupEventsPage.scss';  
  
const { Title, Text } = Typography;  
  
const GroupEventsPage = ({  
  contestant,  
  joinedGroups,  
  setJoinedGroups,  
  selectedGroupEvents,  
  setSelectedGroupEvents,  
  selectedGroupForEvents,  
  setSelectedGroupForEvents  
}) => {  
  const [groupEvents, setGroupEvents] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [createModalOpen, setCreateModalOpen] = useState(false);  
  const [isJoiningEvents, setIsJoiningEvents] = useState(false);  
  
  const state = loadState();  
  
  // Fetch group events  
  useEffect(() => {  
    const fetchGroupEvents = async () => {  
      try {  
        const fetched = await getGroupEvents();  
        setGroupEvents(fetched);  
        if (selectedGroupForEvents) {  
          const appliedEvents = await getApplicationsByGroup(selectedGroupForEvents);  
          const appliedEventIds = appliedEvents.map(app => app.event._id);  
          setSelectedGroupEvents(fetched.filter(event => appliedEventIds.includes(event._id)));  
        }  
      } catch (err) {  
        message.error('Failed to fetch group events');  
      } finally {  
        setLoading(false);  
      }  
    };  
    fetchGroupEvents();  
    // eslint-disable-next-line  
  }, [selectedGroupForEvents]);  
  
  // Fetch own groups (no joined/created, all are 'created' for this scenario)  
  useEffect(() => {  
    const fetchGroups = async () => {  
      try {  
        const role = state.user.role;  
        const fetchedGroups = await searchGroups(role);  
        setJoinedGroups(fetchedGroups.map(group => ({  
          ...group,  
          type: 'created'  
        })));  
      } catch (error) {  
        // Fail silently for now  
      }  
    };  
    fetchGroups();  
    // eslint-disable-next-line  
  }, []);  
  
  // Group events by event name  
  const groupByEventName = (events) => {  
    const grouped = {};  
    events.forEach(event => {  
      if (!grouped[event.name]) grouped[event.name] = [];  
      grouped[event.name].push(event);  
    });  
    return Object.keys(grouped).map(name => ({  
      name,  
      events: grouped[name]  
    }));  
  };  
  const groupedEvents = groupByEventName(groupEvents).map(event => ({  
    key: event.name,  
    label: event.name,  
    children: (  
      <Row gutter={[16, 16]}>  
        {groupEvents.filter(e => e.name === event.name).map(event => (  
          <Col xs={24} sm={12} md={8} key={event._id}>  
            <label>  
              <input  
                type="checkbox"  
                checked={selectedGroupEvents.some(t => t._id === event._id)}  
                onChange={() => {  
                  const already = selectedGroupEvents.some(ev => ev._id === event._id);  
                  if (already) {  
                    setSelectedGroupEvents(selectedGroupEvents.filter(ev => ev._id !== event._id));  
                  } else {  
                    setSelectedGroupEvents([...selectedGroupEvents, event]);  
                  }  
                }}  
              />  
              {event.style}  
            </label>  
          </Col>  
        ))}  
      </Row>  
    )  
  }));  
  
  // Handle events submit  
  const handleSubmitGroupEvents = async () => {  
    try {  
      setIsJoiningEvents(true);  
      // Existing applications  
      const existingApps = await getApplicationsByGroup(selectedGroupForEvents);  
      const existingEventIds = existingApps.map(app => app.event._id);  
      // New to add  
      const newApps = selectedGroupEvents.filter(event => !existingEventIds.includes(event._id));  
      // Delete those not selected  
      const deleteApps = existingApps.filter(app =>  
        !selectedGroupEvents.some(event => event._id === app.event._id)  
      );  
      // Max events allowed logic can be added here if you wish  
  
      // Add applications  
      const createPromises = newApps.map(event =>  
        createApplication({  
          event: event._id,  
          group: selectedGroupForEvents,  
          competition: event.competition,  
          created_by: contestant.created_by,  
          province: state.user.activeProvince  
        })  
      );  
      // Delete applications  
      const deletePromises = deleteApps.map(app => deleteApplication(app._id));  
  
      await Promise.all([...createPromises, ...deletePromises]);  
      // Refresh group apps for this group  
      const updatedGroupApps = await getApplicationsByGroup(selectedGroupForEvents);  
  
      setJoinedGroups(joinedGroups.map(group =>  
        group._id === selectedGroupForEvents  
          ? { ...group, appliedEvents: updatedGroupApps.map(app => app.event) }  
          : group  
      ));  
      message.success('Group applications updated!');  
      setSelectedGroupEvents([]);  
      setSelectedGroupForEvents(null);  
    } catch (error) {  
      message.error('Error updating group applications.');  
    } finally {  
      setIsJoiningEvents(false);  
    }  
  };  
  
  // Group creation success handler  
  const handleGroupCreated = (newGroup) => {  
    setJoinedGroups([...joinedGroups, { ...newGroup, type: 'created' }]);  
    setCreateModalOpen(false);  
  };  
  
  // Render  
  return (  
    <Space direction="vertical" style={{ width: '100%' }}>  
      <Title level={4}>Your Groups</Title>  
      <Row gutter={[16, 16]} className="group-cards-row">  
        {joinedGroups.map((group, index) => (  
          <Col key={group._id || index} span={24}>  
            <Card className="group-card" title="Group Details">  
              <Space direction="vertical" style={{ width: '100%' }} size="middle">  
                <Title level={4} style={{ margin: 0 }}>{group.name || group.group_name || group._id}</Title>  
                {group.appliedEvents && group.appliedEvents.length > 0 ? (  
                  <List  
                    header={<Text strong>Applied Events ({group.appliedEvents.length})</Text>}  
                    dataSource={group.appliedEvents}  
                    renderItem={event => (  
                      <List.Item key={event._id}>  
                        <List.Item.Meta title={event.name} description={event.style} />  
                      </List.Item>  
                    )}  
                  />  
                ) : (  
                  <Text type="secondary">  
                    You have not selected any events for your group. Click the "Select Events" button to add events to your group.  
                  </Text>  
                )}  
                <Flex>  
                  <Button  
                    icon={<PlusOutlined />}  
                    type="primary"  
                    onClick={() => setSelectedGroupForEvents(group._id)}  
                  >  
                    Select Events  
                  </Button>  
                  <Button  
                    icon={<DeleteOutlined />}  
                    type="danger"  
                    style={{ marginLeft: 8 }}  
                    onClick={() => {  
                      Modal.confirm({  
                        title: 'Delete this group?',  
                        content: 'This action cannot be undone.',  
                        onOk: async () => {  
                          try {  
                            await deleteGroup(group._id);  
                            setJoinedGroups(joinedGroups.filter(g => g._id !== group._id));  
                            message.success('Group deleted.');  
                          } catch {  
                            message.error('Failed to delete group.');  
                          }  
                        }  
                      });  
                    }}  
                  >  
                    Delete Group  
                  </Button>  
                </Flex>  
              </Space>  
            </Card>  
          </Col>  
        ))}  
        <Col span={24}>  
          <Button  
            type="primary"  
            icon={<PlusOutlined />}  
            onClick={() => setCreateModalOpen(true)}  
            style={{ marginTop: 24 }}  
          >  
            Create New Group  
          </Button>  
        </Col>  
      </Row>  
  
      <CreateGroupModal  
        visible={createModalOpen}  
        onClose={() => setCreateModalOpen(false)}  
        onGroupCreated={handleGroupCreated}  
      />  
  
      {selectedGroupForEvents && (  
        <Modal  
          open={!!selectedGroupForEvents}  
          onCancel={() => setSelectedGroupForEvents(null)}  
          title="Select Group Events"  
          okText="Join Events"  
          onOk={handleSubmitGroupEvents}  
          width={800}  
          confirmLoading={isJoiningEvents}  
          destroyOnClose  
        >  
          <Collapse  
            accordion  
            items={groupedEvents}  
            style={{ backgroundColor: 'white' }}  
            expandIcon={({ isActive }) => (!isActive ? <PlusOutlined /> : <MinusOutlined />)}  
            defaultActiveKey={groupedEvents[0]?.key}  
          />  
        </Modal>  
      )}  
    </Space>  
  );  
};  
  
export default GroupEventsPage;  